import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  LogOut, Settings, Users, Activity, ClipboardCheck, 
  Bell, ArrowLeft, Shield, User, Mail, Target, AlertTriangle, Database, FileSearch, Plug
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/hooks/useUserRole';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminLayout = () => {
  const { signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { role, isAdmin, loading } = useUserRole();

  // Redirect non-admins
  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/recruiter');
    }
  }, [isAdmin, loading, navigate]);

  const adminNavItems = [
    { path: '/admin', label: 'Overview', icon: Shield, exact: true },
    { path: '/admin/users', label: 'Users & Roles', icon: Users },
    { path: '/admin/masters', label: 'Entity Schema', icon: Database },
    { path: '/admin/parsing', label: 'Parsing Settings', icon: FileSearch },
    { path: '/admin/integrations', label: 'Integrations', icon: Plug },
    { path: '/admin/activity-logs', label: 'Activity Logs', icon: Activity },
    { path: '/admin/data-quality', label: 'Data Quality', icon: ClipboardCheck },
    { path: '/admin/scoring', label: 'AI Scoring', icon: Target },
    { path: '/admin/notifications', label: 'Notifications', icon: Bell },
    { path: '/admin/email-settings', label: 'Email Settings', icon: Mail },
    { path: '/admin/errors', label: 'Error Management', icon: AlertTriangle },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
    { path: '/admin/profile', label: 'My Profile', icon: User },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b">
            <Link to="/admin" className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-destructive" />
              <span className="text-xl font-bold text-destructive">
                Admin Panel
              </span>
            </Link>
          </div>

          {/* Back to App */}
          <div className="p-4 border-b">
            <Link to="/recruiter">
              <Button variant="outline" className="w-full justify-start">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to App
              </Button>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {adminNavItems.map(item => {
                const Icon = item.icon;
                const isActive = item.exact 
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                
                return (
                  <li key={item.path}>
                    <Link to={item.path}>
                      <Button
                        variant={isActive ? 'secondary' : 'ghost'}
                        className={cn(
                          'w-full justify-start',
                          isActive && 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                        )}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t">
            <div className="flex items-center justify-between mb-3 px-2">
              <div>
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-destructive font-medium capitalize">{role}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => signOut()}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64">
        <div className="px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
